import React, { useMemo } from 'react';
import { FaFileContract, FaHandshake, FaBuilding, FaChartBar, FaCity, FaProjectDiagram, FaUserTie } from 'react-icons/fa';
import { C, SEC_LABELS, REC_STATUTS, DUREE_BANDS } from '../utils/constants';
import { fmt, fmtM, fmtUnit, fmtDate } from '../utils/helpers';

// unit : hérite de sectionUnits pour les graphiques/tableaux imprimés
import BarChart from '../charts/BarChart';
import TableauMensuel from '../tables/TableauMensuel';
import TableauSynthese from '../tables/TableauSynthese';
import { ProjetKpiCard, StackedBarProjet } from './ProjetSection';
import CommercialKpiCard from './CommercialSection';

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const fmtPeriode = (d1, d2) => {
  const f = s => s ? new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
  return `${f(d1)} → ${f(d2)}`;
};

const COLS_VENTES = [
  { key: 'id',          label: 'Contrat' },
  { key: 'dateReserv',  label: 'Date',          fmt: v => fmtDate(v) },
  { key: 'description', label: 'Bien' },
  { key: 'type',        label: 'Type' },
  { key: 'prixNet',     label: 'Prix Net (MAD)', right: true, fmt: v => fmt(v) },
  { key: 'commercial',  label: 'Commercial' },
  { key: 'clients',     label: 'Client(s)' },
];
const COLS_VENTES_COM = [
  { key: 'id',          label: 'Contrat' },
  { key: 'dateReserv',  label: 'Date',          fmt: v => fmtDate(v) },
  { key: 'description', label: 'Bien' },
  { key: 'projet',      label: 'Projet' },
  { key: 'type',        label: 'Type' },
  { key: 'prixNet',     label: 'Prix Net (MAD)', right: true, fmt: v => fmt(v) },
  { key: 'clients',     label: 'Client(s)' },
];
const COLS_REC = [
  { key: 'id_contrat',    label: 'Contrat' },
  { key: 'dateReglement', label: 'Date règl.',    fmt: v => fmtDate(v) },
  { key: 'type',          label: 'Type' },
  { key: 'modeRegl',      label: 'Mode' },
  { key: 'montant',       label: 'Montant (MAD)', right: true, fmt: v => fmt(v) },
  { key: 'statut',        label: 'Statut' },
];
const COLS_PN = [
  { key: 'id',          label: 'Contrat' },
  { key: 'datePn',      label: 'Date PN',        fmt: v => fmtDate(v) },
  { key: 'description', label: 'Bien' },
  { key: 'type',        label: 'Type' },
  { key: 'prixNet',     label: 'Prix Net (MAD)', right: true, fmt: v => fmt(v) },
];
const COLS_TABLEAU = [
  { key: 'type',         label: 'Type' },
  { key: 'caTotal',      label: 'CA Total',      right: true, fmt: v => fmt(v) },
  { key: 'nbVentes',     label: 'Nb V.',         right: true },
  { key: 'caPn',         label: 'CA PN',         right: true, fmt: v => fmt(v) },
  { key: 'nbPn',         label: 'Nb PN',         right: true },
  { key: 'recouvrement', label: 'Recouvrement',  right: true, fmt: v => fmt(v) },
];

/* ── Composants de base ──────────────────────────────────────────────────── */
const Section = ({ title, detail, children }) => (
  <div className={detail ? 'pl-section pl-section-detail' : 'pl-section'}>
    <div className="pl-section-title">{title}</div>
    {children}
  </div>
);

const KpiMiniCard = ({ icon, label, budget, real, pct, color, rows }) => (
  <div className="pl-kpi-card" style={{ borderTop: `3px solid ${color}` }}>
    <div className="pl-kpi-card-head" style={{ color }}>
      <span style={{ marginRight: 6 }}>{icon}</span>{label}
    </div>
    <div className="pl-kpi-row">
      <span className="pl-kpi-label">Budget</span>
      <span className="pl-kpi-val">{fmtM(budget)}</span>
    </div>
    <div className="pl-kpi-row">
      <span className="pl-kpi-label">Réalisé</span>
      <span className="pl-kpi-val" style={{ color, fontWeight: 700 }}>{fmtM(real)}</span>
    </div>
    <div className="pl-kpi-row">
      <span className="pl-kpi-label">Taux</span>
      <span className="pl-kpi-val" style={{ color: pct >= 100 ? C.green : pct >= 75 ? C.amber : '#c62828', fontWeight: 700 }}>
        {pct != null ? `${pct}%` : '—'}
      </span>
    </div>
    {rows?.length > 0 && (
      <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
        {rows.map(r => (
          <div key={r.label} className="pl-kpi-row">
            <span className="pl-kpi-label" style={{ color: '#777' }}>{r.label}</span>
            <span className="pl-kpi-val" style={{ color: r.color || '#333' }}>{r.value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const SimpleTable = ({ cols, rows, noDataMsg = 'Aucune donnée', tfoot }) => {
  if (!rows?.length) return <div style={{ color: '#aaa', fontSize: '0.78rem', padding: '8px 0' }}>{noDataMsg}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="pl-table">
        <thead>
          <tr>{cols.map(c => <th key={c.key} style={{ textAlign: c.right ? 'right' : 'left' }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c.key} style={{ textAlign: c.right ? 'right' : 'left' }}>
                  {c.fmt ? c.fmt(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {tfoot && <tfoot>{tfoot}</tfoot>}
      </table>
    </div>
  );
};

/* ── NotaireSoonPrint — version statique print ───────────────────────────── */
const STATUT_NS_CFG = {
  SIGNE:    { label: 'Signé notaire',     color: '#1b5e20', bg: '#e8f5e9' },
  EN_COURS: { label: 'Signature en cours', color: '#e65100', bg: '#fff3e0' },
  CHEZ:     { label: 'Signé client',       color: '#1565c0', bg: '#e3f2fd' },
  NOT_YET:  { label: 'Pas encore signé',   color: '#6a1b9a', bg: '#f3e5f5' },
};
function getNsStatutKey(row) {
  if (row.bienStatut === 'SIGNE NOTAIRE') return 'SIGNE';
  if (row.enCoursSignature) return 'EN_COURS';
  if (row.chezNotaire) return 'CHEZ';
  return 'NOT_YET';
}

const NotaireSoonPrint = ({ rows = [] }) => {
  if (!rows.length) return <div style={{ color: '#aaa', fontSize: '0.78rem', padding: '8px 0' }}>Aucun dossier en cours chez le notaire</div>;

  const prixTotal = rows.reduce((s, r) => s + r.prixNet, 0);
  const avance    = rows.reduce((s, r) => s + r.totalAvance, 0);
  const reliquat  = rows.reduce((s, r) => s + r.reliquat, 0);
  const avancePct = prixTotal > 0 ? Math.round(avance / prixTotal * 100) : 0;

  const statutCounts = { SIGNE: 0, EN_COURS: 0, CHEZ: 0, NOT_YET: 0 };
  rows.forEach(r => statutCounts[getNsStatutKey(r)]++);

  const byProjet = {};
  rows.forEach(r => {
    const k = r.projetNom || '—';
    if (!byProjet[k]) byProjet[k] = { nb: 0, prixTotal: 0, avance: 0, reliquat: 0 };
    byProjet[k].nb++;
    byProjet[k].prixTotal += r.prixNet;
    byProjet[k].avance    += r.totalAvance;
    byProjet[k].reliquat  += r.reliquat;
  });
  const projetRows = Object.entries(byProjet).sort((a, b) => b[1].nb - a[1].nb);

  return (
    <div>
      {/* KPI globaux */}
      <div className="pl-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="pl-kpi-card" style={{ borderTop: '3px solid #1b3a5f' }}>
          <div className="pl-kpi-card-head" style={{ color: '#1b3a5f' }}>Dossiers</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1b3a5f', lineHeight: 1, marginBottom: 8 }}>{rows.length}</div>
          {Object.entries(statutCounts).filter(([, v]) => v > 0).map(([k, v]) => (
            <div key={k} className="pl-kpi-row">
              <span className="pl-kpi-label" style={{ color: STATUT_NS_CFG[k].color }}>{STATUT_NS_CFG[k].label}</span>
              <span style={{ fontWeight: 700, color: STATUT_NS_CFG[k].color }}>{v}</span>
            </div>
          ))}
        </div>
        <div className="pl-kpi-card" style={{ borderTop: '3px solid #2e7d32' }}>
          <div className="pl-kpi-card-head" style={{ color: '#2e7d32' }}>Valeur totale</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#2e7d32' }}>{fmtM(prixTotal)}</div>
        </div>
        <div className="pl-kpi-card" style={{ borderTop: `3px solid ${C.green}` }}>
          <div className="pl-kpi-card-head" style={{ color: C.green }}>Avance versée</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: C.green }}>{fmtM(avance)}</div>
          <div className="pl-kpi-row">
            <span className="pl-kpi-label">Taux</span>
            <span style={{ fontWeight: 700, color: C.green }}>{avancePct}%</span>
          </div>
        </div>
        <div className="pl-kpi-card" style={{ borderTop: '3px solid #c62828' }}>
          <div className="pl-kpi-card-head" style={{ color: '#c62828' }}>Reliquat</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#c62828' }}>{fmtM(reliquat)}</div>
          <div className="pl-kpi-row">
            <span className="pl-kpi-label">Reste à enc.</span>
            <span style={{ fontWeight: 700, color: '#c62828' }}>{100 - avancePct}%</span>
          </div>
        </div>
      </div>

      {/* Synthèse par projet */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Synthèse par projet
      </div>
      <table className="pl-table" style={{ marginBottom: 16 }}>
        <thead><tr>
          <th>Projet</th>
          <th style={{ textAlign: 'right' }}>Nb dossiers</th>
          <th style={{ textAlign: 'right' }}>Prix total</th>
          <th style={{ textAlign: 'right' }}>Avance</th>
          <th style={{ textAlign: 'right' }}>Reliquat</th>
        </tr></thead>
        <tbody>
          {projetRows.map(([nom, r]) => (
            <tr key={nom}>
              <td style={{ fontWeight: 600, color: C.primary }}>{nom}</td>
              <td style={{ textAlign: 'right' }}>{r.nb}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtM(r.prixTotal)}</td>
              <td style={{ textAlign: 'right', color: C.green, fontWeight: 600 }}>{fmtM(r.avance)}</td>
              <td style={{ textAlign: 'right', color: r.reliquat > 0 ? '#c62828' : '#aaa', fontWeight: 600 }}>{fmtM(r.reliquat)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr style={{ background: '#f5ede4', fontWeight: 700 }}>
          <td>Total</td>
          <td style={{ textAlign: 'right' }}>{rows.length}</td>
          <td style={{ textAlign: 'right' }}>{fmtM(prixTotal)}</td>
          <td style={{ textAlign: 'right', color: C.green }}>{fmtM(avance)}</td>
          <td style={{ textAlign: 'right', color: '#c62828' }}>{fmtM(reliquat)}</td>
        </tr></tfoot>
      </table>

      {/* Tableau détaillé complet */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Détail complet — {rows.length} dossier{rows.length > 1 ? 's' : ''}
      </div>
      <table className="pl-table">
        <thead><tr>
          <th>Dossier</th>
          <th>Date vente</th>
          <th>Projet</th>
          <th>Type bien</th>
          <th>Bien</th>
          <th style={{ textAlign: 'right' }}>Prix Net</th>
          <th style={{ textAlign: 'right' }}>Avance</th>
          <th style={{ textAlign: 'right' }}>Reliquat</th>
          <th>Statut</th>
          <th>Client(s)</th>
          <th>Commercial</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const sk = getNsStatutKey(r);
            const scfg = STATUT_NS_CFG[sk];
            return (
              <tr key={i}>
                <td style={{ fontWeight: 700, color: C.primary, whiteSpace: 'nowrap' }}>{r.numDossier || `#${r.venteId}`}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.dateReservation)}</td>
                <td>{r.projetNom}</td>
                <td>{r.bienType}</td>
                <td style={{ fontSize: '0.68rem' }}>{[r.bienTranche, r.bienImmeuble, r.bienEtage, r.bienNumero].filter(Boolean).join('/') || '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtM(r.prixNet)}</td>
                <td style={{ textAlign: 'right', color: C.green, fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtM(r.totalAvance)}</td>
                <td style={{ textAlign: 'right', color: r.reliquat > 10 ? '#c62828' : '#aaa', fontWeight: r.reliquat > 10 ? 700 : 400, whiteSpace: 'nowrap' }}>{fmtM(r.reliquat)}</td>
                <td><span style={{ fontSize: '0.62rem', fontWeight: 700, background: scfg.bg, color: scfg.color, borderRadius: 8, padding: '1px 6px', whiteSpace: 'nowrap' }}>{scfg.label}</span></td>
                <td style={{ fontSize: '0.7rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.clientsNoms || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{r.commercial || '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr style={{ background: '#f5ede4', fontWeight: 700 }}>
          <td colSpan={5}>{rows.length} dossier{rows.length > 1 ? 's' : ''}</td>
          <td style={{ textAlign: 'right' }}>{fmtM(prixTotal)}</td>
          <td style={{ textAlign: 'right', color: C.green }}>{fmtM(avance)}</td>
          <td style={{ textAlign: 'right', color: '#c62828' }}>{fmtM(reliquat)}</td>
          <td colSpan={3} />
        </tr></tfoot>
      </table>
    </div>
  );
};

/* ── RecouvrementPrint — version statique print ──────────────────────────── */
const RecouvrementPrint = ({ detailRec = [], ptfAvant = 0, ptfApres = 0 }) => {
  const { segments, total, projetRows } = useMemo(() => {
    const totals = {};
    REC_STATUTS.forEach(s => { totals[s.key] = { montant: 0, nb: 0 }; });
    const projets = {};
    detailRec.forEach(r => {
      const u  = (r.statut || '').toUpperCase();
      const st = REC_STATUTS.find(s => s.values.includes(u));
      if (!st) return;
      totals[st.key].montant += r.montant || 0;
      totals[st.key].nb++;
      const proj = r.projet || '—';
      if (!projets[proj]) projets[proj] = {};
      projets[proj][st.key] = (projets[proj][st.key] || 0) + (r.montant || 0);
    });
    const total    = REC_STATUTS.reduce((s, st) => s + totals[st.key].montant, 0);
    const segments = REC_STATUTS.map(s => ({ ...s, value: totals[s.key].montant, nb: totals[s.key].nb, pct: total > 0 ? Math.round(totals[s.key].montant / total * 100) : 0 }));
    const projetRows = Object.entries(projets).map(([projet, vals]) => ({
      projet,
      ...Object.fromEntries(REC_STATUTS.map(s => [s.key, vals[s.key] || 0])),
      total: REC_STATUTS.reduce((sum, s) => sum + (vals[s.key] || 0), 0),
    })).sort((a, b) => b.total - a.total);
    return { segments, total, projetRows };
  }, [detailRec]);

  if (!detailRec.length) return <div style={{ color: '#aaa', fontSize: '0.78rem', padding: '8px 0' }}>Aucune donnée de recouvrement</div>;

  return (
    <div>
      {/* Cartes par statut */}
      <div className="pl-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 16 }}>
        {segments.map(s => (
          <div key={s.key} className="pl-kpi-card" style={{ borderTop: `3px solid ${s.color}`, background: s.bg }}>
            <div className="pl-kpi-card-head" style={{ color: s.color }}>{s.label}</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: s.color, lineHeight: 1.1 }}>{fmtM(s.value)}</div>
            <div className="pl-kpi-row">
              <span className="pl-kpi-label">{s.nb} règl.</span>
              <span style={{ fontWeight: 700, color: s.color }}>{s.pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {(ptfAvant > 0 || ptfApres > 0) && (
        <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 12, display: 'flex', gap: 16 }}>
          {ptfAvant > 0 && <span>PTF cumulé <strong style={{ color: '#888' }}>avant</strong> période : <strong style={{ color: '#1565c0' }}>{fmtM(ptfAvant)}</strong></span>}
          {ptfApres > 0 && <span>PTF cumulé <strong style={{ color: '#888' }}>après</strong> période : <strong style={{ color: '#1565c0' }}>{fmtM(ptfApres)}</strong></span>}
        </div>
      )}

      {/* Synthèse par projet */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Synthèse par projet
      </div>
      <table className="pl-table">
        <thead><tr>
          <th>Projet</th>
          {REC_STATUTS.map(s => <th key={s.key} style={{ textAlign: 'right', color: s.color }}>{s.label}</th>)}
          <th style={{ textAlign: 'right' }}>Total</th>
        </tr></thead>
        <tbody>
          {projetRows.map(row => (
            <tr key={row.projet}>
              <td style={{ fontWeight: 600, color: '#333' }}>{row.projet}</td>
              {REC_STATUTS.map(s => (
                <td key={s.key} style={{ textAlign: 'right', color: s.color, fontWeight: row[s.key] > 0 ? 600 : 400 }}>
                  {row[s.key] > 0 ? fmt(row[s.key]) : '—'}
                </td>
              ))}
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(row.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr style={{ background: '#f5ede4', fontWeight: 700 }}>
          <td>TOTAL</td>
          {segments.map(s => <td key={s.key} style={{ textAlign: 'right', color: s.color }}>{fmt(s.value)}</td>)}
          <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
        </tr></tfoot>
      </table>
    </div>
  );
};

/* ── DureeTraitementPrint — version statique print ───────────────────────── */
const DureeTraitementPrint = ({ data }) => {
  const stats = data?.stats;
  if (!stats) return <div style={{ color: '#aaa', fontSize: '0.78rem', padding: '8px 0' }}>Aucune donnée</div>;

  return (
    <div>
      {/* Stats globales */}
      <div className="pl-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 12 }}>
        {[
          { label: 'Nb dossiers', value: stats.nbDossiers ?? '—', color: '#1b3a5f' },
          { label: 'Durée moy.', value: stats.avgJours != null ? `${stats.avgJours} j` : '—', color: stats.avgJours <= 30 ? '#2e7d32' : stats.avgJours <= 60 ? '#1565c0' : stats.avgJours <= 90 ? '#e65100' : '#c62828' },
          { label: 'Minimum', value: stats.minJours != null ? `${stats.minJours} j` : '—', color: '#2e7d32' },
          { label: 'Maximum', value: stats.maxJours != null ? `${stats.maxJours} j` : '—', color: '#c62828' },
        ].map(({ label, value, color }) => (
          <div key={label} className="pl-kpi-card" style={{ borderTop: `3px solid ${color}`, textAlign: 'center' }}>
            <div className="pl-kpi-card-head" style={{ color, justifyContent: 'center' }}>{label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Répartition par tranche */}
      <div className="pl-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {DUREE_BANDS.map(b => (
          <div key={b.key} className="pl-kpi-card" style={{ background: b.bg, borderTop: `3px solid ${b.color}`, textAlign: 'center' }}>
            <div className="pl-kpi-card-head" style={{ color: b.color, justifyContent: 'center' }}>{b.label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: b.color, lineHeight: 1 }}>{stats[b.key] ?? 0}</div>
            <div style={{ fontSize: '0.68rem', color: b.color, marginTop: 3 }}>
              {stats.nbDossiers > 0 ? `${Math.round((stats[b.key] || 0) / stats.nbDossiers * 100)}%` : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Helper graphiques par projet ────────────────────────────────────────── */
const buildMonthly = (rows, dateKey, valueKey, labels) => {
  const map = {};
  rows.forEach(r => {
    const raw = r[dateKey];
    if (!raw) return;
    const month = String(raw).slice(0, 7);
    map[month] = (map[month] || 0) + (Number(r[valueKey]) || 0);
  });
  return (labels || []).map(l => map[l] || 0);
};

/* ── Bloc par projet (mode groupé) ───────────────────────────────────────── */
const ProjetBlock = ({ projetData, d, sections }) => {
  const nom    = projetData.projet;
  const ventes = d.detailVentes.filter(r => r.projet === nom);
  const rec    = d.detailRec.filter(r => r.projet === nom);
  const pn     = d.detailPn.filter(r => r.projet === nom);
  const tableau = d.sorted.filter(r => r.projet === nom);

  return (
    <div className="pl-group-block">
      <div className="pl-group-title">
        <FaProjectDiagram size={13} style={{ marginRight: 8 }} />{nom}
      </div>

      {sections.has('kpi') && (
        <div className="pl-kpi-grid" style={{ marginBottom: 16 }}>
          <KpiMiniCard icon={<FaFileContract size={12} />} label="Ventes"           color={C.primary}
            budget={projetData.budgetVente} real={projetData.caVentes}  pct={projetData.pctVente} />
          <KpiMiniCard icon={<FaHandshake size={12} />}    label="Recouvrement"     color={C.green}
            budget={projetData.budgetRec}   real={projetData.realisRec} pct={projetData.pctRec}   />
          <KpiMiniCard icon={<FaBuilding size={12} />}     label="Passages Notaire" color={C.purple}
            budget={projetData.budgetPn}    real={projetData.realisPn}  pct={projetData.pctPn}    />
        </div>
      )}

      {sections.has('graphiques') && d.graphique?.labels?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="pl-subsec-title">Graphiques mensuels</div>
          {[
            { label: 'CA Ventes',     rows: ventes, dateKey: 'dateReserv',   valKey: 'prixNet', color: C.primary },
            { label: 'Recouvrement',  rows: rec,    dateKey: 'dateReglement', valKey: 'montant', color: C.green  },
            { label: 'Livraisons PN', rows: pn,     dateKey: 'datePn',        valKey: 'prixNet', color: C.purple },
          ].map(({ label: gl, rows, dateKey, valKey, color }) => {
            const realise = buildMonthly(rows, dateKey, valKey, d.graphique.labels);
            if (realise.every(v => v === 0)) return null;
            return (
              <div key={gl} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: '0.7rem', color: '#777', marginBottom: 4, fontWeight: 600 }}>{gl}</div>
                <BarChart labels={d.graphique.labels} realise={realise} budget={[]}
                  loading={false} color1={color} color2="#e0e0e0" labelR={gl.toUpperCase()} labelB="" unit={d.sectionUnits?.['graphiques'] || 'default'} />
              </div>
            );
          })}
        </div>
      )}

      {sections.has('tableau') && tableau.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="pl-subsec-title">Détail par type</div>
          <SimpleTable cols={COLS_TABLEAU} rows={tableau} />
        </div>
      )}

      {sections.has('detail-ventes') && ventes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="pl-subsec-title">Ventes</div>
          <SimpleTable cols={COLS_VENTES} rows={ventes} />
        </div>
      )}

      {sections.has('detail-rec') && rec.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="pl-subsec-title">Recouvrement</div>
          <SimpleTable cols={COLS_REC} rows={rec} />
        </div>
      )}

      {sections.has('detail-pn') && pn.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="pl-subsec-title">Passages Notaire</div>
          <SimpleTable cols={COLS_PN} rows={pn} />
        </div>
      )}
    </div>
  );
};

/* ── Bloc par commercial (mode groupé) ───────────────────────────────────── */
const CommercialBlock = ({ comData, d, sections }) => {
  const nom    = comData.commercial;
  const ventes = d.detailVentes.filter(r => r.commercial === nom);

  return (
    <div className="pl-group-block">
      <div className="pl-group-title">
        <FaUserTie size={13} style={{ marginRight: 8 }} />{nom}
      </div>

      {sections.has('kpi') && (
        <div className="pl-kpi-grid" style={{ marginBottom: 16 }}>
          <KpiMiniCard icon={<FaFileContract size={12} />} label="Ventes"           color={C.primary}
            budget={comData.budgetVente} real={comData.caVentes}  pct={comData.pctVente}
            rows={[{ label: 'Nb Ventes', value: fmt(comData.nbVentes) }]}
          />
          <KpiMiniCard icon={<FaHandshake size={12} />}    label="Recouvrement"     color={C.green}
            budget={comData.budgetRec}   real={comData.realisRec} pct={comData.pctRec} />
          <KpiMiniCard icon={<FaBuilding size={12} />}     label="Passages Notaire" color={C.purple}
            budget={comData.budgetPn}    real={comData.realisPn}  pct={comData.pctPn}
            rows={[{ label: 'Nb PN', value: fmt(comData.nbPn) }]}
          />
        </div>
      )}

      {sections.has('graphiques') && d.graphique?.labels?.length > 0 && (() => {
        const realise = buildMonthly(ventes, 'dateReserv', 'prixNet', d.graphique.labels);
        if (realise.every(v => v === 0)) return null;
        return (
          <div style={{ marginBottom: 14 }}>
            <div className="pl-subsec-title">CA Ventes mensuel</div>
            <BarChart labels={d.graphique.labels} realise={realise} budget={[]}
              loading={false} color1={C.primary} color2="#e0e0e0" labelR="CA VENTES" labelB="" unit={d.sectionUnits?.['graphiques'] || 'default'} />
          </div>
        );
      })()}

      {sections.has('detail-ventes') && ventes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="pl-subsec-title">Ventes</div>
          <SimpleTable cols={COLS_VENTES_COM} rows={ventes} />
        </div>
      )}
    </div>
  );
};

/* ── En-tête commun ──────────────────────────────────────────────────────── */
const PrintHeader = ({ title, d, badge }) => {
  const activeFilters = (d.activeFilterPills || []).filter(p => p.key !== 'period');
  return (
    <div className="pl-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <img src="/iconshems.png" alt="Shems" style={{ height: 52, objectFit: 'contain' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.35rem', color: C.primary }}>{title || 'Rapport Décisionnel'}</div>
          <div style={{ fontSize: '0.82rem', color: '#555', marginTop: 3 }}>Période : {fmtPeriode(d.debut, d.fin)}</div>
          {badge && (
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: '0.68rem', background: C.greenBg, color: C.green, border: `1px solid ${C.border}`, borderRadius: 10, padding: '1px 10px', fontWeight: 600 }}>{badge}</span>
            </div>
          )}
          {activeFilters.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
              {activeFilters.map(p => (
                <span key={p.key} style={{ fontSize: '0.65rem', background: C.greenBg, color: C.green, borderRadius: 10, padding: '1px 8px', border: `1px solid ${C.border}` }}>{p.label}</span>
              ))}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '0.62rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 }}>Généré le</div>
          <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
            {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Layout général ───────────────────────────────────────────────────────── */
const GeneralLayout = ({ d, sections }) => (
  <>
    {sections.has('kpi') && d.kpi && (
      <Section title={SEC_LABELS['kpi']}>
        <div className="pl-kpi-grid">
          <KpiMiniCard icon={<FaFileContract size={13} />} label="Ventes" color={C.primary}
            budget={d.kpi.budgetVente} real={d.kpi.realisVente} pct={d.kpi.pctVente}
            rows={[
              { label: 'CA Lots Uniques', value: fmtM(d.kpi.caLotsUniques), color: C.primary },
              { label: 'Nb Lots Uniques', value: fmt(d.kpi.nbLotsUniques) },
              { label: 'CA Associations', value: fmtM(d.kpi.caAssociations), color: C.blue },
              { label: 'Nb Parts Asso',   value: fmt(d.kpi.nbPartsAsso) },
            ]}
          />
          <KpiMiniCard icon={<FaHandshake size={13} />} label="Recouvrement" color={C.green}
            budget={d.kpi.budgetRec} real={d.kpi.realisRec} pct={d.kpi.pctRec}
            rows={[
              { label: 'PTF',      value: fmtM(d.kpi.encPtf),    color: C.blue },
              { label: 'Versé',    value: fmtM(d.kpi.encVerse),   color: '#6a1b9a' },
              { label: 'Encaissé', value: fmtM(d.kpi.encEncaise), color: C.green },
              { label: 'Impayé',   value: fmtM(d.kpi.encImpaye),  color: '#c62828' },
            ]}
          />
          <KpiMiniCard icon={<FaBuilding size={13} />} label="Passages Notaire" color={C.purple}
            budget={d.kpi.budgetPn} real={d.kpi.realisPn} pct={d.kpi.pctPn}
            rows={[
              { label: 'Nb PN',             value: fmt(d.kpi.nbPn),             color: C.purple },
              { label: 'Lots chez notaire',  value: fmt(d.kpi.pnActifNbLots),    color: C.purple },
              { label: 'Avance totale',      value: fmtM(d.kpi.pnActifAvance),   color: C.green },
              { label: 'Reliquat total',     value: fmtM(d.kpi.pnActifReliquat), color: '#c62828' },
            ]}
          />
        </div>
      </Section>
    )}

    {sections.has('projets') && d.projetKpi.length > 0 && (
      <Section title={SEC_LABELS['projets']}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {d.projetKpi.filter(r => r.budgetVente > 0 || r.caVentes > 0 || r.realisRec > 0 || r.realisPn > 0).map((item, idx) => (
            <ProjetKpiCard key={item.projetId} item={item} idx={idx} />
          ))}
        </div>
      </Section>
    )}

    {sections.has('commerciaux') && d.commercialKpi.length > 0 && (
      <Section title={SEC_LABELS['commerciaux']}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {d.commercialKpi.filter(r => r.caVentes > 0 || r.realisRec > 0 || r.realisPn > 0).map((item, idx) => (
            <CommercialKpiCard key={item.comId} item={item} idx={idx} />
          ))}
        </div>
      </Section>
    )}

    {sections.has('ca-projet') && d.projetKpi.length > 0 && (
      <Section title={SEC_LABELS['ca-projet']}>
        <StackedBarProjet data={d.projetKpi} loading={false} />
      </Section>
    )}

    {sections.has('graphiques') && d.graphique && (
      <Section title={SEC_LABELS['graphiques']}>
        {[
          { title: 'CA Réalisé vs Budget',                realise: d.graphique.realise   || [], budget: d.graphique.budget    || [], color1: '#1565c0', color2: '#9e9e9e', dataType: 'ventes'       },
          { title: 'Évolution mensuelle — Livraisons',    realise: d.graphique.realisPn  || [], budget: d.graphique.budgetPn  || [], color1: '#1565c0', color2: '#f9a825', dataType: 'pn'           },
          { title: 'Évolution mensuelle — Encaissements', realise: d.graphique.realisRec || [], budget: d.graphique.budgetRec || [], color1: '#1565c0', color2: '#d84315', dataType: 'recouvrement' },
        ].map(({ title: gt, realise, budget, color1, color2, dataType }) => (
          <div key={gt} style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 700, color: C.primary, fontSize: '0.88rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaChartBar size={12} />{gt}
            </div>
            <BarChart labels={d.graphique.labels || []} realise={realise} budget={budget} loading={false} color1={color1} color2={color2} labelR="RÉALISATIONS" labelB="BUDGET" unit={d.sectionUnits?.['graphiques'] || 'default'} />
            <TableauMensuel graphique={{ labels: d.graphique.labels || [], realise, budget }} debut={d.debut} fin={d.fin} dataType={dataType} onCellClick={() => {}} onBudgetClick={() => {}} unit={d.sectionUnits?.['graphiques'] || 'default'} />
          </div>
        ))}
      </Section>
    )}

    {sections.has('analyse-rec') && (
      <Section title={SEC_LABELS['analyse-rec']}>
        <RecouvrementPrint detailRec={d.detailRec} ptfAvant={d.ptfAvant} ptfApres={d.ptfApres} />
      </Section>
    )}

    {sections.has('synthese') && d.budgetParType.length > 0 && (() => {
      const VILLE_COLORS = { casablanca: '#264061', marrakech: '#964c4d', benslimane: '#013b23' };
      const getVilleColor = (name) => {
        const n = (name || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        return VILLE_COLORS[Object.keys(VILLE_COLORS).find(k => n.includes(k))] || null;
      };
      const PROJET_NUANCES = {
        '#264061': ['#264061', '#2e5073', '#1a3050', '#35628a'],
        '#964c4d': ['#964c4d', '#7d3f40', '#a85759', '#b96c6d'],
        '#013b23': ['#013b23', '#025e37', '#012a1a', '#026b41'],
      };
      const byVille = {};
      d.budgetParType.forEach(r => {
        if (!byVille[r.villeId]) byVille[r.villeId] = { villeId: r.villeId, ville: r.ville, projets: {} };
        const v = byVille[r.villeId];
        if (!v.projets[r.projetId]) v.projets[r.projetId] = { projetId: r.projetId, projet: r.projet, rows: [] };
        v.projets[r.projetId].rows.push(r);
      });
      const villes = Object.values(byVille).sort((a, b) => a.ville.localeCompare(b.ville));
      return (
        <Section title={SEC_LABELS['synthese']}>
          {villes.map(villeGroup => {
            const vc = getVilleColor(villeGroup.ville);
            const nuances = PROJET_NUANCES[vc] || [];
            return (
              <div key={villeGroup.villeId} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 800, color: vc || '#264061', borderBottom: `2px solid ${vc || '#264061'}`, paddingBottom: 6, marginBottom: 10 }}>
                  {villeGroup.ville}
                </div>
                {Object.values(villeGroup.projets).sort((a, b) => b.rows.reduce((s, r) => s + r.realisRec, 0) - a.rows.reduce((s, r) => s + r.realisRec, 0)).map((projetGroup, pi) => {
                  const pc = nuances[pi % nuances.length] || vc || '#5c3317';
                  return (
                    <div key={projetGroup.projetId} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', background: pc, borderRadius: '4px 4px 0 0', padding: '4px 12px', marginBottom: 0 }}>
                        {projetGroup.projet}
                      </div>
                      <TableauSynthese data={projetGroup.rows} loading={false} fin={d.fin} budgetCA={d.budgetParTypeCA?.[projetGroup.projetId]} unit={d.sectionUnits?.['synthese'] || 'default'} onCellClick={() => {}} />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Section>
      );
    })()}

    {sections.has('duree-traitement') && d.dureeData && (
      <Section title={SEC_LABELS['duree-traitement']}>
        <DureeTraitementPrint data={d.dureeData} />
      </Section>
    )}

    {sections.has('notaire-soon') && (
      <Section title={SEC_LABELS['notaire-soon']}>
        <NotaireSoonPrint rows={d.nsRows} />
      </Section>
    )}

    {sections.has('tableau') && d.sorted.length > 0 && (
      <Section title={SEC_LABELS['tableau']} detail>
        <SimpleTable cols={[{ key: 'projet', label: 'Projet' }, ...COLS_TABLEAU]} rows={d.sorted} />
      </Section>
    )}

    {sections.has('detail-ventes') && d.detailVentes.length > 0 && (
      <Section title={SEC_LABELS['detail-ventes']} detail>
        <SimpleTable cols={COLS_VENTES} rows={d.detailVentes} />
      </Section>
    )}

    {sections.has('detail-rec') && d.detailRec.length > 0 && (
      <Section title={SEC_LABELS['detail-rec']} detail>
        <SimpleTable cols={COLS_REC} rows={d.detailRec} />
      </Section>
    )}

    {sections.has('detail-pn') && d.detailPn.length > 0 && (
      <Section title={SEC_LABELS['detail-pn']} detail>
        <SimpleTable cols={COLS_PN} rows={d.detailPn} />
      </Section>
    )}
  </>
);

/* ── Composant principal ─────────────────────────────────────────────────── */
const PrintLayout = React.forwardRef(({ d, sections, title, groupBy = 'general' }, ref) => {
  const projetsActifs = useMemo(
    () => (d?.projetKpi || []).filter(r => r.budgetVente > 0 || r.caVentes > 0 || r.realisRec > 0 || r.realisPn > 0),
    [d],
  );
  const comsActifs = useMemo(
    () => (d?.commercialKpi || []).filter(r => r.caVentes > 0 || r.realisRec > 0 || r.realisPn > 0),
    [d],
  );
  const villeGroups = useMemo(() => {
    if (groupBy !== 'ville' || !d) return null;
    const map = {};
    projetsActifs.forEach(p => {
      const fp       = d.filters?.projets?.find(fp => fp.id === p.projetId);
      const cityId   = fp?.cityId;
      const cityName = d.filters?.villes?.find(v => v.id === cityId)?.label || 'Autres';
      if (!map[cityName]) map[cityName] = [];
      map[cityName].push(p);
    });
    return Object.entries(map);
  }, [groupBy, projetsActifs, d]);

  if (!d) return null;

  return (
    <div ref={ref} className="print-layout">

      {groupBy === 'general' && (
        <>
          <PrintHeader title={title} d={d} />
          <GeneralLayout d={d} sections={sections} />
        </>
      )}

      {groupBy === 'projet' && (
        <>
          <PrintHeader title={title} d={d} badge={`${projetsActifs.length} projet${projetsActifs.length > 1 ? 's' : ''}`} />
          {projetsActifs.map(p => <ProjetBlock key={p.projetId} projetData={p} d={d} sections={sections} />)}
        </>
      )}

      {groupBy === 'ville' && villeGroups && (
        <>
          <PrintHeader title={title} d={d} badge={`${villeGroups.length} ville${villeGroups.length > 1 ? 's' : ''}`} />
          {villeGroups.map(([villeName, projets]) => (
            <div key={villeName} className="pl-ville-block">
              <div className="pl-ville-title">
                <FaCity size={14} style={{ marginRight: 8 }} />{villeName}
                <span style={{ fontSize: '0.72rem', fontWeight: 400, marginLeft: 10, opacity: 0.7 }}>
                  {projets.length} projet{projets.length > 1 ? 's' : ''}
                </span>
              </div>
              {projets.map(p => <ProjetBlock key={p.projetId} projetData={p} d={d} sections={sections} />)}
            </div>
          ))}
        </>
      )}

      {groupBy === 'commercial' && (
        <>
          <PrintHeader title={title} d={d} badge={`${comsActifs.length} commercial${comsActifs.length > 1 ? 'x' : ''}`} />
          {comsActifs.map(c => <CommercialBlock key={c.comId} comData={c} d={d} sections={sections} />)}
        </>
      )}

    </div>
  );
});

PrintLayout.displayName = 'PrintLayout';
export default PrintLayout;
